import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ImageSlant from '../../common/image-slant';
import heroImage from '../../../assets/christmas25/grotto.webp';
import searchImage from '../../../assets/christmas25/search.webp';
import teaImage from '../../../assets/christmas25/tea2.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 1024px) { height: 420px; }
  @media (max-width: 768px) { height: 340px; }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 6px rgba(0,0,0,0.35);
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 2.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: 1rem;
  }
`;

const Card = styled.section.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  z-index: 1;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    left: 0; bottom: 0;
    width: 80px; height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const Body = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;
  p { margin: 0 0 1rem; }
`;

const Notice = styled.div.attrs({ className: 'bf-surface' })`
  background: rgba(157, 109, 73, 0.1);
  border: 1px solid rgba(157, 109, 73, 0.3);
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin: 1rem 0 0.5rem;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
`;

const BookingNotice = styled(Notice)`
  background: rgba(208, 56, 45, 0.12);
  border-color: rgba(208, 56, 45, 0.4);
  color: ${props => props.theme.colors.primary};
  display: grid;
  gap: 0.6rem;
  strong {
    font-size: 1.05rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  ul {
    margin: 0;
    padding-left: 1.25rem;
    font-weight: 500;
  }
  li { margin: 0.15rem 0; }
`;

const AccentLink = styled.a`
  color: ${props => props.theme.colors.accent};
  font-weight: 700;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const InfoItem = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  padding: 1rem 1.25rem;
  border-radius: 8px;
`;

const InfoLabel = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const GalleryImage = styled.div`
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  height: 220px;
`;

const CTAButton = styled.a.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  margin-top: 1.25rem;
  transition: all 0.2s ease;
  &:hover { opacity: 0.95; transform: translateY(-2px); }
`;

const SantasGrotto = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const meta = {
    title: "Santa's Grotto at Belvoir Retail Village 2025 | Christmas Events",
    description: "Meet Santa in his magical grotto at Belvoir Retail Village this Christmas 2025. Family Christmas activities in Leicestershire with Santa's Grotto. Book now!",
    canonical: 'https://www.belvoircastle.com/christmas/santas-grotto'
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: "Santa's Grotto at Belvoir Retail Village Christmas 2025",
    description: "Meet Santa in his magical grotto at Belvoir Retail Village. Available 20th-23rd December 2025. A heartwarming Christmas family experience with private family visits, presents, and photo opportunities in a beautifully decorated festive setting.",
    image: [
      "https://www.belvoircastle.com/christmas25/grotto.webp",
      "https://www.belvoircastle.com/christmas25/search.webp"
    ],
    startDate: '2025-12-20',
    endDate: '2025-12-23',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: 'Belvoir Retail Village',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Belvoir Retail Village',
        addressLocality: 'Grantham',
        addressRegion: 'Leicestershire',
        postalCode: 'NG32 1PE',
        addressCountry: 'GB'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 52.8931,
        longitude: -0.7792
      }
    },
    organizer: {
      '@type': 'Organization',
      name: 'Belvoir Castle',
      url: 'https://www.belvoircastle.com',
      telephone: '+441476871001',
      email: 'info@belvoircastle.com'
    },
    offers: {
      '@type': 'Offer',
      url: 'https://www.belvoircastle.com/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/InStock',
      validFrom: '2025-12-20',
      validThrough: '2025-12-23'
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Families with children'
    },
    keywords: 'Christmas, Santa, Grotto, Family activities, Leicestershire, Christmas events, Belvoir Castle',
    url: meta.canonical
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content="Santa's Grotto, Christmas activities Leicestershire, Christmas family events, Belvoir Castle Christmas, Christmas day out, Santa visit, Christmas grotto, festive activities, Christmas events Lincolnshire" />
        <meta name="author" content="Belvoir Castle" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="geo.region" content="GB-LEI" />
        <meta name="geo.placename" content="Grantham, Leicestershire" />
        <meta name="geo.position" content="52.8931;-0.7792" />
        <meta name="ICBM" content="52.8931, -0.7792" />
        <link rel="canonical" href={meta.canonical} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content="https://www.belvoircastle.com/christmas25/grotto.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content="https://www.belvoircastle.com/christmas25/grotto.webp" />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:url" content={meta.canonical} />
        <meta name="twitter:image:alt" content="Santa's Grotto at Belvoir Retail Village" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#101d40" />
        <meta name="apple-mobile-web-app-title" content="Santa's Grotto at Belvoir Retail Village" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.belvoircastle.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Christmas 2025",
                "item": "https://www.belvoircastle.com/christmas"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "Santa's Grotto",
                "item": meta.canonical
              }
            ]
          })}
        </script>
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>At Belvoir Retail Village</Badge>
          <Title>Santa's Grotto</Title>
          <Strapline>An enchanting visit to Santa at Belvoir Retail Village</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <Card>
          <SectionTitle>About the Experience</SectionTitle>
          <Body>
            <p>Step into a magical moment with Santa at Belvoir Retail Village, just a 5 minute walk from Belvoir Castle. Your family will be welcomed into Santa's grotto for a joyful meet-and-greet and a heart-warming photo opportunity in a beautifully decorated festive setting.</p>
            <p>Each child will receive a special present from Santa, and you'll have plenty of time for photos to capture these precious memories.</p>
            <p>After your grotto visit, explore the Retail Village with its charming shops, dining options, and festive atmosphere. Perfect for families looking for a special Christmas experience.</p>
          </Body>

          <Gallery>
            <GalleryImage src={heroImage} />
            <GalleryImage src={searchImage} />
          </Gallery>
        </Card>

        <Card>
          <SectionTitle>Key Information</SectionTitle>
          <Notice><strong>Location:</strong> This experience is at Belvoir Retail Village, just a 5 minute walk from Belvoir Castle.</Notice>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <div>20th – 23rd December 2025</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <div><strong>Belvoir Retail Village</strong> (a 5 minute walk from Belvoir Castle)</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>What's Included</InfoLabel>
              <div>Visit with Santa, a special present for each child, and photo opportunities in a beautifully decorated festive setting.</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Perfect For</InfoLabel>
              <div>Families with children of all ages looking for a magical Christmas experience.</div>
            </InfoItem>
          </InfoGrid>

          <CTAButton href="/christmas/santas-grotto-retail-village-options">Start Your Booking</CTAButton>
        </Card>
      </Content>

      <ImageSlant 
        image={teaImage}
        title="Festive Afternoon Tea"
        description="Add a delicious seasonal treat to your day with indulgent sweet and savoury bites."
        buttonText="Book Festive Tea"
        buttonAction={() => window.location.assign('/christmas/festive-afternoon-tea')}
        imagePosition="left"
        darkMode={isBlackFriday}
      />

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2025" />
      </div>
    </Page>
  );
};

export default SantasGrotto;

